import { CellContext, createColumnHelper } from '@tanstack/react-table';
import React, { useState } from 'react';
import { LuUserCog } from 'react-icons/lu';

import { Portfolio } from '@ndvr/portfolio-api';

import { CopyGuidDropdownMenuItem } from '@/components/CopyGuidComponents';
import { Card } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { EllipsisDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import {
    formatDate,
    PORTFOLIO_TYPE_DISPLAY_MAP,
    PORTFOLIO_TYPE_TAG_MAP,
    useHouseholdRoutePaths,
    useRequiredParams,
} from 'utils';
import { ChangeAccountOwnerDrawer } from 'views/household/AccountGroupTab/ChangeAccountOwnerDrawer';
import { useAccount } from 'views/portfolio/account-queries';
import { usePortfolios } from 'views/portfolio/portfolio-queries';
import { PortfolioReviewStatus } from 'views/portfolio/PortfolioReviewStatus';
import { CondensedTable, PageSpinner, Tag, Text, TextLink, VerticalLayout } from 'widgets';

const columnHelper = createColumnHelper<Portfolio>();

export const UnlinkedPortfoliosList = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { PortfolioPageRoutePath } = useHouseholdRoutePaths();
    const { data: portfolios, isPending: portfoliosLoading } = usePortfolios(globalAccountGUID, undefined, false);

    const filteredPortfolios = portfolios?.filter(({ originPortfolioGUID }) => !originPortfolioGUID) ?? [];

    const columns = [
        columnHelper.accessor('portfolioType', {
            id: 'type',
            header: 'Type',
            cell: ({ getValue }) => (
                <Tag type={PORTFOLIO_TYPE_TAG_MAP[getValue()]}>{PORTFOLIO_TYPE_DISPLAY_MAP[getValue()]}</Tag>
            ),
        }),
        columnHelper.accessor((data) => data.name.plainText, {
            id: 'name',
            header: 'Name',
            cell: ({ getValue, row }) => (
                <TextLink to={PortfolioPageRoutePath(globalAccountGUID, row.original.accountGroupGUID)}>
                    {getValue()}
                </TextLink>
            ),
        }),
        columnHelper.accessor('updatedAt', {
            id: 'lastUpdated',
            header: 'Last Updated',
            cell: ({ getValue }) => <Text>{formatDate(getValue())}</Text>,
            sortingFn: 'datetime',
        }),
        columnHelper.accessor((data) => data.reviewStatus, {
            id: 'reviewStatus',
            header: 'Status',
            cell: ({ getValue }) => <PortfolioReviewStatus reviewStatus={getValue()} />,
        }),
        columnHelper.display({
            id: 'actions',
            header: '',
            // A stable component reference preserves the open drawer across table re-renders.
            cell: UnlinkedPortfolioActions,
            size: 50,
        }),
    ];

    return (
        <VerticalLayout $gap={16}>
            <h2 className="text-cocoa-1000 text-xl">Accounts without Custody Account</h2>
            <Card>
                {portfoliosLoading ? (
                    <PageSpinner />
                ) : (
                    <CondensedTable
                        data={filteredPortfolios}
                        columns={columns}
                        sort={{ col: 'lastUpdated', desc: true }}
                    />
                )}
            </Card>
        </VerticalLayout>
    );
};

const UnlinkedPortfolioActions = ({ row }: CellContext<Portfolio, unknown>) => {
    const portfolio = row.original;
    const { data: account } = useAccount(portfolio.portfolioGUID);
    const [showChangeAccountOwnerDrawer, setShowChangeAccountOwnerDrawer] = useState(false);

    return (
        <>
            <DropdownMenu>
                <EllipsisDropdownMenuTrigger ariaLabel={`Actions for ${portfolio.name.plainText}`} />
                <DropdownMenuContent>
                    <DropdownMenuItem disabled={!account} onClick={() => setShowChangeAccountOwnerDrawer(true)}>
                        <LuUserCog className="mr-2 h-4 w-4" />
                        Change account owner
                    </DropdownMenuItem>
                    <CopyGuidDropdownMenuItem guid={portfolio.portfolioGUID} />
                </DropdownMenuContent>
            </DropdownMenu>
            {showChangeAccountOwnerDrawer && account && (
                <ChangeAccountOwnerDrawer
                    householdGUID={portfolio.globalAccountGUID}
                    account={account}
                    onClose={() => setShowChangeAccountOwnerDrawer(false)}
                />
            )}
        </>
    );
};
